import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../shared/DashboardLayout';
// Phase 4 Components
import PatientList from '../patients/PatientList';
import DeviationList from '../deviations/DeviationList';
import './AdministrationDashboard.css';

/**
 * Administration Dashboard
 * Department administration with limited departmental access
 */
const AdministrationDashboard = ({ onLogout, userRole, userData, dashboardConfig }) => {
  const { t } = useTranslation('dashboards');
  const [activeSection, setActiveSection] = useState('department-overview');
  const [userName, setUserName] = useState('');

  useEffect(() => {
    if (userData) {
      setUserName(userData.full_name || `${userData.first_name} ${userData.last_name}`);
    }
  }, [userData]);

  const [expandedSections, setExpandedSections] = React.useState({
    'departments-section': true,
    'tasks-section': false
  });

  const navigationItems = [
    {
      id: 'departments-section',
      label: t('administration.myDepartments'),
      type: 'section',
      expanded: expandedSections['departments-section'],
      children: [
        { id: 'department-overview', label: t('administration.departmentOverview') },
        { id: 'staff-management', label: t('administration.staffManagement') },
        { id: 'resources', label: t('administration.resources') }
      ]
    },
    {
      id: 'patient-section',
      label: t('administration.patientSection'),
      type: 'section',
      expanded: expandedSections['patient-section'],
      children: [
        { id: 'patient-management', label: t('administration.patients') }
      ]
    },
    {
      id: 'tasks-section',
      label: t('management.taskManagement'),
      type: 'section',
      expanded: expandedSections['tasks-section'],
      children: [
        { id: 'create-dept-tasks', label: t('administration.createDeptTasks') },
        { id: 'create-room-tasks', label: t('administration.createRoomTasks') },
        { id: 'assign-tasks', label: t('administration.assignTasks') },
        { id: 'task-overview', label: t('administration.taskOverview') }
      ]
    },
    { type: 'divider' },
    { id: 'compliance', label: t('administration.departmentCompliance'), type: 'item' },
    { id: 'deviations-registry', label: t('administration.standardDeviations'), type: 'item' },
    { id: 'analytics', label: t('administration.departmentAnalytics'), type: 'item' },
    { id: 'scheduling', label: t('administration.scheduling'), type: 'item' }
  ];

  const handleNavigate = (sectionId) => {
    if (sectionId.startsWith('toggle-')) {
      const section = sectionId.replace('toggle-', '');
      setExpandedSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
      return;
    }
    setActiveSection(sectionId);
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'department-overview':
        return (
          <div className="admin-content">
            <h2>{t('administration.departmentOverview')}</h2>
            <p>{t('administration.departmentOverviewDesc')}</p>
            <div className="department-grid">
              <div className="department-card">
                <div className="department-icon">🏨</div>
                <h3>{t('administration.myDepartmentsCard')}</h3>
                <p className="department-count">{t('departmentsCount', { count: 0 })}</p>
                <button className="btn btn-info btn-icon">{t('viewDetails')}</button>
              </div>
            </div>
            <div className="coming-soon">
              <span className="icon">🚧</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'staff-management':
        return (
          <div className="admin-content">
            <h2>{t('administration.staffManagement')}</h2>
            <p>{t('administration.staffManagementDesc')}</p>
            <div className="coming-soon">
              <span className="icon">👥</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'resources':
        return (
          <div className="admin-content">
            <h2>{t('administration.resources')}</h2>
            <p>{t('administration.resourcesDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📦</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'create-dept-tasks':
        return (
          <div className="admin-content">
            <h2>{t('administration.createDeptTasks')}</h2>
            <p>{t('administration.createDeptTasksDesc')}</p>
            <div className="coming-soon">
              <span className="icon">➕</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'create-room-tasks':
        return (
          <div className="admin-content">
            <h2>{t('administration.createRoomTasks')}</h2>
            <p>{t('administration.createRoomTasksDesc')}</p>
            <div className="coming-soon">
              <span className="icon">🚪</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'assign-tasks':
        return (
          <div className="admin-content">
            <h2>{t('administration.assignTasks')}</h2>
            <p>{t('administration.assignTasksDesc')}</p>
            <div className="coming-soon">
              <span className="icon">👤</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'task-overview':
        return (
          <div className="admin-content">
            <h2>{t('administration.taskOverview')}</h2>
            <p>{t('administration.taskOverviewDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📋</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'compliance':
        return (
          <div className="admin-content">
            <h2>{t('administration.departmentCompliance')}</h2>
            <p>{t('administration.departmentComplianceDesc')}</p>
            <div className="coming-soon">
              <span className="icon">✓</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );

      case 'deviations-registry':
        return (
          <DeviationList
            organizationId={userData?.organizationId || userData?.organization_id}
            userId={userData?.uid}
            userRole={userRole}
          />
        );

      case 'analytics':
        return (
          <div className="admin-content">
            <h2>{t('administration.departmentAnalytics')}</h2>
            <p>{t('administration.departmentAnalyticsDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📊</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      case 'scheduling':
        return (
          <div className="admin-content">
            <h2>{t('administration.scheduling')}</h2>
            <p>{t('administration.schedulingDesc')}</p>
            <div className="coming-soon">
              <span className="icon">📅</span>
              <p>{t('comingSoon')}</p>
            </div>
          </div>
        );
      
      // Phase 4: Patient Management Section
      case 'patient-management':
        return <PatientList departmentFilter={userData?.primary_department_id} />;
      
      default:
        return (
          <div className="admin-content">
            <h2>{t('administration.title')}</h2>
            <p>{t('administration.welcome')}</p>
          </div>
        );
    }
  };

  return (
    <DashboardLayout
      userRole={userRole}
      userName={userName}
      onLogout={onLogout}
      navigationItems={navigationItems}
      activeSection={activeSection}
      onNavigate={handleNavigate}
      showNotifications={true}
    >
      {renderContent()}
    </DashboardLayout>
  );
};

export default AdministrationDashboard;

